import { EventType, Room, RoomType } from 'matrix-js-sdk/src/matrix';

import { APP_ROUTES } from 'routes/routePaths';
import { CustomMeedioMatrixEvents, isInstantCallRoom } from 'utils/matrixUtils';
import { getRoomStateEvents, isMeetingRoom, isMessengerCallRoom, isWorkspaceRoom } from 'utils/roomHierarchyUtils';

// Routes where the app shell is not mounted: connectivity loss, hard 404, and the
// unsupported-browser gate. Invite UI is deferred on these the same way it is during a call.
const ERROR_OR_BLOCKED_ROUTES: readonly string[] = [
  APP_ROUTES.networkError,
  APP_ROUTES.notFound,
  APP_ROUTES.unsupportedBrowser,
];

export const isErrorOrBlockedRoute = (pathname: string): boolean =>
  ERROR_OR_BLOCKED_ROUTES.some((route) => pathname === route || pathname.startsWith(`${route}/`));

/** Space type is not always set on stripped invite state; fall back to m.room.create content. */
export const isSpaceInviteRoom = (room: Room): boolean => {
  if (room.getType() === RoomType.Space) return true;
  const createContent = room.currentState?.getStateEvents(EventType.RoomCreate, '')?.getContent() ?? {};
  return createContent.type === 'm.space' || createContent.type === RoomType.Space;
};

/** Video-hub scheduled meetings stamp this state event on the space after creation (sendScheduleStateEvent). */
export const isScheduledCallSpace = (room: Room): boolean =>
  getRoomStateEvents(room, CustomMeedioMatrixEvents.IS_SCHEDULED_CALL_STATE_EVENT).length > 0;

/**
 * Video-hub scheduled meeting space the user was Matrix-invited to. Uses signals visible on stripped
 * invite state (`m.space.child` via isMeetingRoom) and falls back to the schedule state event when
 * the client has already synced it. Excludes workspaces, instant calls, and messenger-linked spaces.
 */
export const isVideoHubScheduledMeetingInvite = (room: Room): boolean => {
  if (!isSpaceInviteRoom(room)) return false;
  if (isInstantCallRoom(room)) return false;
  if (isWorkspaceRoom(room)) return false;
  if (isMessengerCallRoom(room)) return false;
  return isMeetingRoom(room) || isScheduledCallSpace(room);
};
